import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Phone, Plus, Upload } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { OverviewToolbar } from "../../components/layout/OverviewToolbar";
import { NumberImportWizard } from "../../components/admin/NumberImportWizard";
import { useSession } from "../../hooks/useSession";
import {
  adminNumbersApi,
  type AdminNumberListItem,
  type PagedNumbers,
} from "../../api/adminNumbersApi";

const PAGE_SIZE = 50;

// The page header has no background, so the buttons use the regular
// .btn styles (same as the customer-side overview pages).
const headerButton: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

/**
 * Cross-customer number overview with server-side paging + search
 * (admins see all numbers, managers only their own). Uses the shared
 * OverviewToolbar + tile-rows look so it matches the other overviews;
 * search is debounced and refetches, "Exact" switches to a dnis prefix
 * match.
 */
export function AdminNumbersPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const isAdmin = session?.kind === "Admin";

  const [search, setSearch] = useState("");
  const [exact, setExact] = useState(false);
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState<PagedNumbers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async (nextSkip: number, q: string, ex: boolean) => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await adminNumbersApi.list({ search: q, exactSearch: ex, skip: nextSkip, take: PAGE_SIZE }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load numbers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(0, "", false);
  }, [load]);

  // Debounced search: reset to first page whenever the query changes.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setSkip(0);
      void load(0, search, exact);
    }, 400);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, exact]);

  const goto = (nextSkip: number) => {
    setSkip(nextSkip);
    void load(nextSkip, search, exact);
  };

  const total = data?.totalCount ?? 0;
  const items = data?.items ?? [];
  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + PAGE_SIZE, total);

  return (
    <>
      <PageHeader
        title="Numbers"
        actions={
          isAdmin ? (
            <div style={{ display: "inline-flex", gap: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setImportOpen(true)} style={headerButton}>
                <Upload size={14} /> Import numbers
              </button>
              <button type="button" className="btn btn-primary" onClick={() => navigate("/admin/numbers/new")} style={headerButton}>
                <Plus size={14} /> New number
              </button>
            </div>
          ) : null
        }
      />
      <div className="dashboard">
        <OverviewToolbar
          search={search}
          onSearch={setSearch}
          placeholder="Search number, description, customer, script…"
          count={{ matched: items.length, total }}
          trailing={
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: "0.9rem" }}>
              <input type="checkbox" checked={exact} onChange={(e) => setExact(e.target.checked)} />
              Exact number prefix
            </label>
          }
        />

        {error && (
          <div className="tile" style={{ borderColor: "#e74c3c", color: "#c0392b", background: "#fdecea" }}>
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading numbers…</div>
        ) : items.length === 0 ? (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>No numbers found.</div>
        ) : (
          <div className="tile-rows">
            {items.map((n) => (
              <NumberRow key={n.dnisId} n={n} onOpen={() => navigate(`/admin/numbers/${n.dnisId}`)} />
            ))}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 4 }}>
          <span style={{ color: "var(--portal-muted)", fontSize: "0.85rem" }}>
            {total === 0 ? "No results" : `${from}–${to} of ${total}`}
            {loading ? " · loading…" : ""}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => goto(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0 || loading}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => goto(skip + PAGE_SIZE)}
              disabled={to >= total || loading}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem" }}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
      <NumberImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => void load(skip, search, exact)}
      />
    </>
  );
}

function NumberRow({ n, onOpen }: { n: AdminNumberListItem; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="tile"
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 16,
        padding: "12px 18px",
        textAlign: "left",
        font: "inherit",
        color: "inherit",
        cursor: "pointer",
        width: "100%",
      }}
    >
      <span
        style={{
          flex: "1.4 1 0",
          minWidth: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontWeight: 600,
          fontSize: "1rem",
        }}
      >
        <Phone size={15} style={{ color: "var(--portal-muted)", flexShrink: 0 }} aria-hidden="true" />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {n.dnisPresentation || n.dnis || "—"}
        </span>
      </span>
      <Detail flex="1.6">{n.description || "—"}</Detail>
      <Detail flex="0.7">{n.numberTypeName || "—"}</Detail>
      <Detail flex="1">{n.countryName || "—"}</Detail>
      <Detail flex="1.2">{n.customerName || "—"}</Detail>
      <span style={{ flexShrink: 0 }}>
        <StatusChip active={n.active} />
      </span>
    </button>
  );
}

function Detail({ children, flex }: { children: React.ReactNode; flex: string }) {
  return (
    <span
      className="tile-row-detail"
      style={{
        flex: `${flex} 1 0`,
        minWidth: 0,
        color: "var(--portal-muted)",
        fontSize: "0.9rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function StatusChip({ active }: { active: boolean }) {
  const styles: React.CSSProperties = active
    ? {
        background: "var(--portal-status-success-soft)",
        color: "var(--portal-status-success-darker)",
        borderColor: "var(--portal-status-success)",
      }
    : {
        background: "var(--portal-surface)",
        color: "var(--portal-muted)",
        borderColor: "var(--portal-border)",
      };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid",
        fontSize: "0.8rem",
        fontWeight: 600,
        ...styles,
      }}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}
